/**
 * Shared hero-content block tokens for builder canvas and email HTML.
 * Section palette core heroes (standard + extended minimal layouts).
 */
export type HeroContentTemplate =
  | 'standard'
  | 'dark-centered'
  | 'split'
  | 'minimal'
  | 'centered-light'
  | 'banner-image'
  | 'accent-line'
  | 'overlay-minimal';

/** Verified stock URLs for hero presets (Unsplash IDs expire occasionally). */
export const HERO_PRESET_IMAGES = {
  standard:
    'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1200&q=80',
  split:
    'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1200&q=80',
  banner:
    'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1200&q=80',
  overlay:
    'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1200&q=80',
} as const;

export const HERO_CORE_TEMPLATES: HeroContentTemplate[] = [
  'standard',
  'dark-centered',
  'split',
  'minimal',
  'centered-light',
  'banner-image',
  'accent-line',
  'overlay-minimal',
];

export function isHeroCoreTemplate(template?: string): template is HeroContentTemplate {
  return HERO_CORE_TEMPLATES.includes(template as HeroContentTemplate);
}

/** Target outer heights for section palette thumbnails (px). */
export const HERO_PALETTE_PREVIEW_HEIGHTS: Record<string, number> = {
  'layout-hero-content': 132,
  'layout-hero-centered-dark': 118,
  'layout-hero-split': 108,
  'layout-hero-minimal': 82,
  'layout-hero-centered-light': 100,
  'layout-hero-banner-image': 112,
  'layout-hero-accent-line': 86,
  'layout-hero-overlay-minimal': 108,
};

const PALETTE_TYPO: Record<
  HeroContentTemplate,
  Pick<HeroContentResolved, 'headingSize' | 'textSize' | 'buttonFontSize' | 'headingWeight'>
> = {
  standard: { headingSize: '13px', textSize: '10px', buttonFontSize: '9px', headingWeight: 700 },
  'dark-centered': { headingSize: '14px', textSize: '10px', buttonFontSize: '9px', headingWeight: 700 },
  split: { headingSize: '13px', textSize: '10px', buttonFontSize: '9px', headingWeight: 700 },
  minimal: { headingSize: '15px', textSize: '10px', buttonFontSize: '9px', headingWeight: 700 },
  'centered-light': { headingSize: '14px', textSize: '10px', buttonFontSize: '9px', headingWeight: 600 },
  'banner-image': { headingSize: '13px', textSize: '10px', buttonFontSize: '9px', headingWeight: 700 },
  'accent-line': { headingSize: '13px', textSize: '10px', buttonFontSize: '9px', headingWeight: 700 },
  'overlay-minimal': { headingSize: '13px', textSize: '9px', buttonFontSize: '9px', headingWeight: 600 },
};

const PALETTE_SPACING: Record<
  HeroContentTemplate,
  Pick<
    HeroContentResolved,
    | 'headingPadding'
    | 'textPadding'
    | 'buttonSectionPadding'
    | 'splitTextPadding'
    | 'splitImagePadding'
    | 'minimalHeadingPadding'
    | 'minimalTextPadding'
    | 'bannerImageMaxHeight'
    | 'overlayMinHeight'
  >
> = {
  standard: {
    headingPadding: '6px 10px 4px',
    textPadding: '0 10px 4px',
    buttonSectionPadding: '0 10px 8px',
    splitTextPadding: '10px 8px',
    splitImagePadding: '0',
    minimalHeadingPadding: '10px 10px 4px',
    minimalTextPadding: '0 10px 8px',
    bannerImageMaxHeight: 52,
    overlayMinHeight: 108,
  },
  'dark-centered': {
    headingPadding: '12px 10px 4px',
    textPadding: '0 10px 6px',
    buttonSectionPadding: '0 10px 10px',
    splitTextPadding: '10px 8px',
    splitImagePadding: '0',
    minimalHeadingPadding: '10px 10px 4px',
    minimalTextPadding: '0 10px 8px',
    bannerImageMaxHeight: 52,
    overlayMinHeight: 108,
  },
  split: {
    headingPadding: '6px 10px 4px',
    textPadding: '0 10px 4px',
    buttonSectionPadding: '0 10px 8px',
    splitTextPadding: '10px 8px',
    splitImagePadding: '0',
    minimalHeadingPadding: '10px 10px 4px',
    minimalTextPadding: '0 10px 8px',
    bannerImageMaxHeight: 52,
    overlayMinHeight: 108,
  },
  minimal: {
    headingPadding: '6px 10px 4px',
    textPadding: '0 10px 4px',
    buttonSectionPadding: '0 10px 8px',
    splitTextPadding: '10px 8px',
    splitImagePadding: '0',
    minimalHeadingPadding: '10px 10px 4px',
    minimalTextPadding: '0 10px 6px',
    bannerImageMaxHeight: 52,
    overlayMinHeight: 108,
  },
  'centered-light': {
    headingPadding: '10px 12px 4px',
    textPadding: '0 12px 6px',
    buttonSectionPadding: '0 12px 10px',
    splitTextPadding: '10px 8px',
    splitImagePadding: '0',
    minimalHeadingPadding: '10px 10px 4px',
    minimalTextPadding: '0 10px 8px',
    bannerImageMaxHeight: 48,
    overlayMinHeight: 108,
  },
  'banner-image': {
    headingPadding: '8px 12px 4px',
    textPadding: '0 12px 4px',
    buttonSectionPadding: '0 12px 8px',
    splitTextPadding: '10px 8px',
    splitImagePadding: '0',
    minimalHeadingPadding: '10px 10px 4px',
    minimalTextPadding: '0 10px 8px',
    bannerImageMaxHeight: 44,
    overlayMinHeight: 108,
  },
  'accent-line': {
    headingPadding: '8px 10px 4px 14px',
    textPadding: '0 10px 4px 14px',
    buttonSectionPadding: '0 10px 8px 14px',
    splitTextPadding: '10px 8px',
    splitImagePadding: '0',
    minimalHeadingPadding: '10px 10px 4px',
    minimalTextPadding: '0 10px 8px',
    bannerImageMaxHeight: 52,
    overlayMinHeight: 108,
  },
  'overlay-minimal': {
    headingPadding: '0 12px 4px',
    textPadding: '0 12px 4px',
    buttonSectionPadding: '0 12px 8px',
    splitTextPadding: '10px 8px',
    splitImagePadding: '0',
    minimalHeadingPadding: '10px 10px 4px',
    minimalTextPadding: '0 10px 8px',
    bannerImageMaxHeight: 52,
    overlayMinHeight: 108,
  },
};

/** Image column height in split hero palette thumbnails. */
export const HERO_PALETTE_SPLIT_IMAGE_HEIGHT = 80;

/** Accent line visibility and width for `accent-line` hero template. */
export function resolveAccentLineOptions(
  content: Record<string, unknown>,
  isPalettePreview = false
): { show: boolean; widthPx: number } {
  const show = content.showAccentLine !== false;
  let widthPx = 4;
  const raw = content.accentLineWidth;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    widthPx = raw;
  } else if (typeof raw === 'string' && raw.trim()) {
    const parsed = parseInt(String(raw).replace(/px$/i, ''), 10);
    if (!Number.isNaN(parsed)) widthPx = parsed;
  }
  widthPx = Math.min(16, Math.max(1, Math.round(widthPx)));
  const displayWidth = isPalettePreview
    ? Math.max(2, Math.min(8, Math.round(widthPx * 0.85)))
    : widthPx;
  return { show, widthPx: displayWidth };
}

/** Use symmetric horizontal padding when the accent bar is hidden. */
export function accentLinePaddingWhenHidden(pad: string): string {
  const parts = pad.trim().split(/\s+/);
  if (parts.length !== 4) return pad;
  const [top, right, bottom] = parts;
  return `${top} ${right} ${bottom} ${right}`;
}

/** Normalize corner radius from properties panel (supports bare numbers → px). */
export function normalizeHeroButtonRadius(value: unknown, fallback: string): string {
  if (value === null || value === undefined) return fallback;
  const raw = String(value).trim();
  if (!raw) return fallback;
  if (/^\d+(\.\d+)?$/.test(raw)) return `${raw}px`;
  return raw;
}

export interface HeroContentResolved {
  template: HeroContentTemplate;
  fontFamily: string;
  containerBg: string;
  contentBg: string;
  splitImageBg: string;
  headingColor: string;
  textColor: string;
  buttonBg: string;
  buttonColor: string;
  buttonRadius: string;
  buttonAlign: 'left' | 'center' | 'right';
  textAlign: 'left' | 'center' | 'right';
  headingSize: string;
  textSize: string;
  buttonFontSize: string;
  headingWeight: number;
  headingPadding: string;
  textPadding: string;
  buttonSectionPadding: string;
  splitTextPadding: string;
  splitImagePadding: string;
  minimalHeadingPadding: string;
  minimalTextPadding: string;
  bannerImageMaxHeight: number;
  overlayMinHeight: number;
}

const templateDefaults: Record<
  HeroContentTemplate,
  Pick<
    HeroContentResolved,
    | 'containerBg'
    | 'contentBg'
    | 'splitImageBg'
    | 'headingColor'
    | 'textColor'
    | 'buttonBg'
    | 'buttonColor'
    | 'buttonRadius'
    | 'buttonAlign'
    | 'textAlign'
    | 'headingWeight'
    | 'bannerImageMaxHeight'
    | 'overlayMinHeight'
  >
> = {
  standard: {
    containerBg: '#FFFFFF',
    contentBg: '#FFFFFF',
    splitImageBg: '#EFF6FF',
    headingColor: '#111827',
    textColor: '#6B7280',
    buttonBg: '#3B82F6',
    buttonColor: '#FFFFFF',
    buttonRadius: '0',
    buttonAlign: 'left',
    textAlign: 'left',
    headingWeight: 700,
    bannerImageMaxHeight: 240,
    overlayMinHeight: 360,
  },
  'dark-centered': {
    containerBg: '#0F172A',
    contentBg: '#0F172A',
    splitImageBg: '#EFF6FF',
    headingColor: '#FFFFFF',
    textColor: '#E5E7EB',
    buttonBg: '#22C55E',
    buttonColor: '#FFFFFF',
    buttonRadius: '9999px',
    buttonAlign: 'center',
    textAlign: 'center',
    headingWeight: 700,
    bannerImageMaxHeight: 240,
    overlayMinHeight: 360,
  },
  split: {
    containerBg: '#FFFFFF',
    contentBg: '#FFFFFF',
    splitImageBg: '#EFF6FF',
    headingColor: '#111827',
    textColor: '#4B5563',
    buttonBg: '#2563EB',
    buttonColor: '#FFFFFF',
    buttonRadius: '0',
    buttonAlign: 'left',
    textAlign: 'left',
    headingWeight: 700,
    bannerImageMaxHeight: 240,
    overlayMinHeight: 360,
  },
  minimal: {
    containerBg: '#F9FAFB',
    contentBg: '#F9FAFB',
    splitImageBg: '#EFF6FF',
    headingColor: '#111827',
    textColor: '#4B5563',
    buttonBg: '#2563EB',
    buttonColor: '#FFFFFF',
    buttonRadius: '0',
    buttonAlign: 'left',
    textAlign: 'left',
    headingWeight: 700,
    bannerImageMaxHeight: 240,
    overlayMinHeight: 360,
  },
  'centered-light': {
    containerBg: '#FFFFFF',
    contentBg: '#FFFFFF',
    splitImageBg: '#EFF6FF',
    headingColor: '#111827',
    textColor: '#6B7280',
    buttonBg: '#111827',
    buttonColor: '#FFFFFF',
    buttonRadius: '0',
    buttonAlign: 'center',
    textAlign: 'center',
    headingWeight: 600,
    bannerImageMaxHeight: 240,
    overlayMinHeight: 360,
  },
  'banner-image': {
    containerBg: '#FFFFFF',
    contentBg: '#FFFFFF',
    splitImageBg: '#F3F4F6',
    headingColor: '#111827',
    textColor: '#6B7280',
    buttonBg: '#111827',
    buttonColor: '#FFFFFF',
    buttonRadius: '0',
    buttonAlign: 'center',
    textAlign: 'center',
    headingWeight: 700,
    bannerImageMaxHeight: 220,
    overlayMinHeight: 360,
  },
  'accent-line': {
    containerBg: '#FFFFFF',
    contentBg: '#FFFFFF',
    splitImageBg: '#EFF6FF',
    headingColor: '#111827',
    textColor: '#6B7280',
    buttonBg: '#111827',
    buttonColor: '#FFFFFF',
    buttonRadius: '0',
    buttonAlign: 'left',
    textAlign: 'left',
    headingWeight: 700,
    bannerImageMaxHeight: 240,
    overlayMinHeight: 360,
  },
  'overlay-minimal': {
    containerBg: '#111827',
    contentBg: 'transparent',
    splitImageBg: '#1F2937',
    headingColor: '#FFFFFF',
    textColor: '#D1D5DB',
    buttonBg: '#FFFFFF',
    buttonColor: '#111827',
    buttonRadius: '0',
    buttonAlign: 'left',
    textAlign: 'left',
    headingWeight: 600,
    bannerImageMaxHeight: 240,
    overlayMinHeight: 340,
  },
};

const CENTERED_TEMPLATES: HeroContentTemplate[] = [
  'dark-centered',
  'centered-light',
  'banner-image',
];

function desktopHeadingSize(template: HeroContentTemplate, isMobile: boolean): string {
  if (isMobile) {
    if (template === 'minimal' || template === 'centered-light') return '28px';
    if (template === 'overlay-minimal') return '26px';
    return '24px';
  }
  const map: Partial<Record<HeroContentTemplate, string>> = {
    minimal: '34px',
    'centered-light': '30px',
    'banner-image': '26px',
    'accent-line': '28px',
    'overlay-minimal': '28px',
    split: '28px',
    'dark-centered': '32px',
    standard: '32px',
  };
  return map[template] || '32px';
}

function desktopTextSize(template: HeroContentTemplate, isMobile: boolean): string {
  if (isMobile) return '14px';
  if (template === 'minimal' || template === 'centered-light') return '15px';
  if (template === 'dark-centered') return '15px';
  if (template === 'overlay-minimal') return '14px';
  return '16px';
}

function desktopPaddings(
  template: HeroContentTemplate,
  isMobile: boolean
): Pick<HeroContentResolved, 'headingPadding' | 'textPadding' | 'buttonSectionPadding' | 'minimalHeadingPadding' | 'minimalTextPadding'> {
  const m = isMobile;
  const pad = (d: string, mob: string) => (m ? mob : d);

  if (template === 'centered-light') {
    return {
      headingPadding: pad('44px 32px 8px 32px', '28px 20px 6px 20px'),
      textPadding: pad('0 32px 20px 32px', '0 20px 16px 20px'),
      buttonSectionPadding: pad('0 32px 44px 32px', '0 20px 28px 20px'),
      minimalHeadingPadding: pad('48px 20px 8px 20px', '32px 20px 8px 20px'),
      minimalTextPadding: pad('0 20px 32px 20px', '0 20px 28px 20px'),
    };
  }
  if (template === 'banner-image') {
    return {
      headingPadding: pad('32px 32px 8px 32px', '24px 20px 6px 20px'),
      textPadding: pad('0 32px 16px 32px', '0 20px 12px 20px'),
      buttonSectionPadding: pad('0 32px 36px 32px', '0 20px 24px 20px'),
      minimalHeadingPadding: pad('48px 20px 8px 20px', '32px 20px 8px 20px'),
      minimalTextPadding: pad('0 20px 32px 20px', '0 20px 28px 20px'),
    };
  }
  if (template === 'accent-line') {
    return {
      headingPadding: pad('40px 40px 8px 44px', '28px 20px 6px 28px'),
      textPadding: pad('0 40px 20px 44px', '0 20px 14px 28px'),
      buttonSectionPadding: pad('0 40px 40px 44px', '0 20px 28px 28px'),
      minimalHeadingPadding: pad('48px 20px 8px 20px', '32px 20px 8px 20px'),
      minimalTextPadding: pad('0 20px 32px 20px', '0 20px 28px 20px'),
    };
  }
  if (template === 'overlay-minimal') {
    return {
      headingPadding: pad('0 32px 8px 32px', '0 20px 6px 20px'),
      textPadding: pad('0 32px 16px 32px', '0 20px 12px 20px'),
      buttonSectionPadding: pad('0 32px 32px 32px', '0 20px 24px 20px'),
      minimalHeadingPadding: pad('48px 20px 8px 20px', '32px 20px 8px 20px'),
      minimalTextPadding: pad('0 20px 32px 20px', '0 20px 28px 20px'),
    };
  }
  if (template === 'dark-centered') {
    return {
      headingPadding: pad('48px 32px 12px 32px', '32px 20px 12px 20px'),
      textPadding: pad('0 32px 24px 32px', '0 20px 24px 20px'),
      buttonSectionPadding: pad('0 32px 40px 32px', '0 20px 32px 20px'),
      minimalHeadingPadding: pad('48px 20px 8px 20px', '32px 20px 8px 20px'),
      minimalTextPadding: pad('0 20px 32px 20px', '0 20px 28px 20px'),
    };
  }
  if (template === 'standard') {
    return {
      headingPadding: pad('40px 40px 20px 40px', '24px 20px 16px 20px'),
      textPadding: pad('0 40px 30px 40px', '0 20px 24px 20px'),
      buttonSectionPadding: pad('0 40px 40px 40px', '0 20px 24px 20px'),
      minimalHeadingPadding: pad('48px 20px 8px 20px', '32px 20px 8px 20px'),
      minimalTextPadding: pad('0 20px 32px 20px', '0 20px 28px 20px'),
    };
  }
  if (template === 'minimal') {
    return {
      headingPadding: pad('48px 20px 8px 20px', '32px 20px 8px 20px'),
      textPadding: pad('0 20px 32px 20px', '0 20px 28px 20px'),
      buttonSectionPadding: pad('0 20px 32px', '0 20px 24px'),
      minimalHeadingPadding: pad('48px 20px 8px 20px', '32px 20px 8px 20px'),
      minimalTextPadding: pad('0 20px 32px 20px', '0 20px 28px 20px'),
    };
  }
  // split + fallback
  return {
    headingPadding: pad('40px 20px 20px 20px', '24px 20px 12px 20px'),
    textPadding: pad('0 20px 20px 20px', '0 20px 16px 20px'),
    buttonSectionPadding: pad('0 20px 32px 20px', '0 20px 24px 20px'),
    minimalHeadingPadding: pad('48px 20px 8px 20px', '32px 20px 8px 20px'),
    minimalTextPadding: pad('0 20px 32px 20px', '0 20px 28px 20px'),
  };
}

export const resolveHeroContentStyles = (
  template: HeroContentTemplate,
  content: Record<string, unknown>,
  styles: Record<string, unknown>,
  globalFontFamily?: string,
  isMobile = false,
  isPalettePreview = false
): HeroContentResolved => {
  const d = templateDefaults[template];
  const align = (styles.textAlign as string) || d.textAlign;
  const compact = isPalettePreview;

  if (compact) {
    const typo = PALETTE_TYPO[template];
    const space = PALETTE_SPACING[template];
    return {
      template,
      fontFamily: (styles.fontFamily as string) || globalFontFamily || 'Arial, sans-serif',
      containerBg: (styles.backgroundColor as string) || d.containerBg,
      contentBg: (content.contentBackgroundColor as string) || d.contentBg,
      splitImageBg: (content.splitImageBackgroundColor as string) || d.splitImageBg,
      headingColor: (content.headingColor as string) || d.headingColor,
      textColor: (content.textColor as string) || d.textColor,
      buttonBg: (content.buttonColor as string) || d.buttonBg,
      buttonColor: (content.buttonTextColor as string) || d.buttonColor,
      buttonRadius: normalizeHeroButtonRadius(content.buttonBorderRadius, d.buttonRadius),
      buttonAlign: CENTERED_TEMPLATES.includes(template)
        ? 'center'
        : template === 'overlay-minimal'
          ? 'left'
          : (align as 'left' | 'center' | 'right'),
      textAlign: CENTERED_TEMPLATES.includes(template)
        ? 'center'
        : (align as 'left' | 'center' | 'right'),
      ...typo,
      ...space,
    };
  }

  const pads = desktopPaddings(template, isMobile);
  const headingWeight =
    typeof content.headingWeight === 'number'
      ? content.headingWeight
      : d.headingWeight;

  return {
    template,
    fontFamily: (styles.fontFamily as string) || globalFontFamily || 'Arial, sans-serif',
    containerBg: (styles.backgroundColor as string) || d.containerBg,
    contentBg: (content.contentBackgroundColor as string) || d.contentBg,
    splitImageBg: (content.splitImageBackgroundColor as string) || d.splitImageBg,
    headingColor: (content.headingColor as string) || d.headingColor,
    textColor: (content.textColor as string) || d.textColor,
    buttonBg: (content.buttonColor as string) || d.buttonBg,
    buttonColor: (content.buttonTextColor as string) || d.buttonColor,
    buttonRadius: normalizeHeroButtonRadius(content.buttonBorderRadius, d.buttonRadius),
    buttonAlign:
      template === 'standard' && !isMobile
        ? 'left'
        : CENTERED_TEMPLATES.includes(template)
          ? 'center'
          : (align as 'left' | 'center' | 'right'),
    textAlign: CENTERED_TEMPLATES.includes(template)
      ? 'center'
      : (align as 'left' | 'center' | 'right'),
    headingSize: desktopHeadingSize(template, isMobile),
    textSize: desktopTextSize(template, isMobile),
    buttonFontSize: isMobile ? '14px' : template === 'dark-centered' ? '15px' : '14px',
    headingWeight,
    ...pads,
    splitTextPadding: isMobile ? '24px 20px' : '40px 20px 32px',
    splitImagePadding: isMobile ? '24px 20px' : '24px 20px 32px',
    bannerImageMaxHeight:
      typeof content.bannerImageHeight === 'number'
        ? content.bannerImageHeight
        : d.bannerImageMaxHeight,
    overlayMinHeight:
      typeof content.overlayMinHeight === 'number'
        ? content.overlayMinHeight
        : d.overlayMinHeight,
  };
};
